import React from 'react';
import { unified } from 'unified';
import rehypeParse from 'rehype-parse';
import rehypeReact from 'rehype-react';
import { renderTextWithFootnotesReferencesV2 } from '../feature-service-types';
import {
  Headline,
  Text,
  Divider,
  TextList,
  TextListItem,
  TextLink,
  RichText,
} from '@oneaudi/unified-web-components';
import { SYS_SPACE_FIXED_100, SYS_SPACE_FIXED_200 } from '@oneaudi/unified-web-common';
import * as prod from 'react/jsx-runtime';
import { getValidLinkAriaLabel, processFootnoteHTML } from '../../utils/ariaLabelHelper';

import type { Options } from 'rehype-react';
import type { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

const SPACING_RELATIVE_MD = 'var(--spacing-relative-md)';
const SPACING_RELATIVE_2_XL = 'var(--spacing-relative-2xl)';

const StyledHeadline = styled(Headline)`
  sup {
    color: inherit;
    font-size: 0.583em;
  }
`;

const StyledText = styled(Text)``;

const StyledTextList = styled(TextList)`
  li:before {
    content: '>';
    text-align: end;
    display: inline-block;
    width: ${SYS_SPACE_FIXED_100};
    position: relative;
    margin-right: ${SYS_SPACE_FIXED_200};
  }
  sup {
    color: inherit;
    font-size: 0.583em;
  }
`;

const StyledTextListItem = styled(TextListItem)``;

const StyledTextLink = styled(TextLink)``;

interface Props {
  style?: { [key: string]: string };
  href?: string;
  target?: string;
  title?: string;
  ariaLabel?: string;
}

const StyledOrderedList = styled(TextList)``;
const isHtml = /<[a-z/][\s\S]*>/i;
const defaultOptions = { Fragment: prod.Fragment, jsx: prod.jsx, jsxs: prod.jsxs };
const rehypeReactOptions = {
  ...defaultOptions,
  components: {
    h1(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h1"
          variant={{
            type: 'headline',
            order: '1',
            weight: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h2(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h2"
          variant={{
            type: 'headline',
            order: '2',
            weight: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h3(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h3"
          variant={{
            type: 'headline',
            order: '3',
            weight: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h4(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h4"
          variant={{
            type: 'headline',
            order: '4',
            weight: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h5(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h5"
          variant={{
            type: 'headline',
            order: '4',
            weight: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    h6(props: React.PropsWithChildren) {
      return (
        <StyledHeadline
          tag="h6"
          variant={{
            type: 'headline',
            order: '4',
            weight: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          {processChildren(props.children)}
        </StyledHeadline>
      );
    },
    p(props: React.PropsWithChildren) {
      return (
        <StyledText
          tag="p"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          {processChildren(props.children)}
        </StyledText>
      );
    },
    div(props: React.PropsWithChildren) {
      return (
        <StyledText
          tag="div"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
        >
          {processChildren(props.children)}
        </StyledText>
      );
    },
    span(props: React.PropsWithChildren) {
      return (
        <StyledText
          tag="span"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
        >
          {processChildren(props.children)}
        </StyledText>
      );
    },
    ul(props: React.PropsWithChildren) {
      return (
        <StyledTextList variant="bullet" margin={[0, 0, SPACING_RELATIVE_MD, SPACING_RELATIVE_MD]}>
          {processChildren(props.children)}
        </StyledTextList>
      );
    },
    ol(props: React.PropsWithChildren) {
      return (
        <StyledOrderedList
          variant="decimal"
          margin={[0, 0, SPACING_RELATIVE_MD, SPACING_RELATIVE_MD]}
        >
          {processChildren(props.children)}
        </StyledOrderedList>
      );
    },
    li(props: React.PropsWithChildren) {
      return <StyledTextListItem>{processChildren(props.children)}</StyledTextListItem>;
    },
    hr() {
      return <Divider margin={[SPACING_RELATIVE_2_XL, 0, SPACING_RELATIVE_2_XL, 0]} />;
    },
    blockquote({ children, ...props }: React.PropsWithChildren) {
      return (
        <blockquote {...props}>
          <StyledText
            tag="p"
            variant={{
              type: 'copy',
              order: '1',
              style: 'normal',
            }}
          >
            {processChildren(children)}
          </StyledText>
        </blockquote>
      );
    },
    pre(props: React.PropsWithChildren) {
      return (
        <StyledText
          tag="span"
          variant={{
            type: 'copy',
            order: '1',
            style: 'normal',
          }}
          margin={[0, 0, SPACING_RELATIVE_MD, 0]}
        >
          <pre>{processChildren(props.children)}</pre>
        </StyledText>
      );
    },
    a({ children, ...props }: React.PropsWithChildren) {
      const { href, target, title } = props as Props;
      const linkText = typeof children === 'string' ? children : '';

      // Check if this is a footnote link (href starts with #)
      const isFootnoteLink = href && href.startsWith('#');
      let validAriaLabel: string;

      if (isFootnoteLink) {
        // For footnote links, generate appropriate aria-label
        if (linkText.trim()) {
          validAriaLabel = `Footnote: ${linkText.trim()}`;
        } else {
          const footnoteId = href.substring(1);
          validAriaLabel = `Footnote reference ${footnoteId}`;
        }
      } else {
        // For regular links, use the original logic
        validAriaLabel = getValidLinkAriaLabel(title, linkText, href);
      }

      return (
        <StyledTextLink
          href={href as string}
          size="md"
          variant="inline"
          target={target}
          aria-label={validAriaLabel}
          title={title}
        >
          {processChildren(children)}
        </StyledTextLink>
      );
    },
    strong({ children }: React.PropsWithChildren) {
      return (
        <StyledText
          tag="span"
          variant={{
            type: 'copy',
            order: '1',
            style: 'bold',
          }}
        >
          {processChildren(children)}
        </StyledText>
      );
    },
  },
};
const processChildren = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: string | ReactNode | ReactElement<any>[],
) => {
  if (typeof children === 'string') {
    return processChild(children);
  }
  if (typeof children === 'string' && isHtml.test(children)) {
    return <RichText markdown={children} />;
  }
  if (Array.isArray(children)) {
    return children.map((child) => processChild(child as unknown as string));
  }
  return children;
};
const processChild = (value: string) => {
  if (typeof value === 'string' && value.match(/({ft_[a-z0-9-_]*})/gi)) {
    const footnoteContent = renderTextWithFootnotesReferencesV2(value);
    return processFootnoteHTML(footnoteContent);
  }
  if (typeof value === 'string' && isHtml.test(value)) {
    return <RichText markdown={value} />;
  }
  return value;
};

export const convertHtmlToReact = (html: string) => {
  const file = unified()
    .use(rehypeParse, {
      fragment: true,
    })
    .use(rehypeReact, rehypeReactOptions as unknown as Options)
    .processSync(html);

  return file.result;
};
